import { FC, useState } from "react";

import { useTranslation } from "react-i18next";
import kebabCase from "lodash/kebabCase";
import * as yup from "yup";

import { issuers } from "../../helpers/duitnow-pay";

import { usePaymentMethod } from "../../contexts/PaymentMethodContext";

import Dropdown, { DropdownOption } from "../../components/Dropdown";
import FormField from "../../components/FormField";

import Button from "../../components/Button/Button";
import { usePaymentRequest } from "../../contexts/PaymentRequestContext";
import {
  ExternalAnalyticsEvent,
  ExternalAnalyticsProvider,
  InternalAnalyticsEvent,
  logExternalAnalyticsEvent,
  logInternalAnalyticsEvent
} from "../../utils/analytics";
import { useFormik } from "formik";
import { PaymentChannelsPropertiesType } from "@xendit/checkout-utilities";

type DuitnowPayMode = "RETAIL" | "CORPORATE";

type IssuerOption = {
  name: string;
  label: string;
  value: string;
  display_name: string;
  disabled: boolean;
  tooltipMessage: undefined;
};

export type DuitnowPayAccordionProps = {
  channels: PaymentChannelsPropertiesType[];
};

const DuitnowPayAccordionContent: FC<DuitnowPayAccordionProps> = () => {
  const { t } = useTranslation(["duitnow-pay-dd", "common"]);

  const { selectPaymentChannel } = usePaymentMethod();
  const { initializing } = usePaymentRequest();

  const [duitnowPayMode, setDuitnowPayMode] =
    useState<DuitnowPayMode>("RETAIL");

  const getIssuers = (mode: DuitnowPayMode) => {
    const issuerList =
      mode === "CORPORATE" ? issuers.corporate : issuers.retail;
    return issuerList.map((issuer) => ({
      name: issuer.name,
      label: issuer.label,
      value: issuer.value,
      display_name: issuer.name,
      disabled: issuer.isActive === false,
      tooltipMessage: undefined
    }));
  };

  const { values, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      accountHolderType: "RETAIL",
      selectedBank: null as IssuerOption | null
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSubmit: async (values: any) => {
      if (initializing) {
        return;
      }

      // send external analytics event on form submission
      logInternalAnalyticsEvent({
        event: InternalAnalyticsEvent.SUBMIT_PAYMENT
      });

      // send external submit analytics event on form submission
      logExternalAnalyticsEvent({
        event_name: ExternalAnalyticsEvent.SUBMIT,
        target: [
          ExternalAnalyticsProvider.FACEBOOK,
          ExternalAnalyticsProvider.GOOGLE
        ]
      });

      selectPaymentChannel("DD_DUITNOW_PAY", {
        accountHolderType: values.accountHolderType,
        issuerName: values.selectedBank.name
      });
    },
    validationSchema: yup.object({
      accountHolderType: yup
        .string()
        .required(
          t("{{field}} is required", { field: t("Account Holder Type") })
        ),
      selectedBank: yup
        .object()
        .nullable()
        .required(t("{{field}} is required", { field: t("Select Bank") }))
    })
  });

  const switchMode = (mode: DuitnowPayMode) => {
    if (duitnowPayMode !== mode) {
      setFieldValue("selectedBank", null);
      setDuitnowPayMode(mode);
    }
  };

  return (
    <>
      <div className="py-6 flex justify-center">
        <div className="w-full max-w-md px-4 flex flex-col space-y-6">
          <div className="w-full flex flex-row justify-center">
            <div className="flex items-center justify-center">
              <label className="text-center mx-auto text-xl font-bold">
                {t("Pay with DuitNow Pay")}
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <FormField
              label={t("Account Holder Type")}
              name="accountHolderType"
            >
              <Dropdown
                data-testid="select-duitnow-pay-account-holder-type"
                options={["RETAIL", "CORPORATE"]}
                keyExtractor={(option) => option as string}
                value={values.accountHolderType}
                onChange={(mode: unknown) => {
                  const selectedMode = mode as DuitnowPayMode;
                  setFieldValue("accountHolderType", selectedMode);
                  switchMode(selectedMode);
                }}
                block
                placeholder={t("Select Account Holder Type")}
                renderOption={(option, helpers) => {
                  const mode = option as DuitnowPayMode;
                  const displayText =
                    mode === "RETAIL" ? t("Retail") : t("Corporate");

                  return (
                    <div className="w-full">
                      <DropdownOption
                        {...helpers}
                        className="flex items-center space-x-2"
                        data-testid={kebabCase(mode.toLowerCase())}
                      >
                        <span>{displayText}</span>
                      </DropdownOption>
                    </div>
                  );
                }}
                renderSelection={(selection) => {
                  const mode = selection as DuitnowPayMode;
                  const displayText =
                    mode === "RETAIL" ? t("Retail") : t("Corporate");

                  return (
                    <div className="flex items-center space-x-2">
                      <span>{displayText}</span>
                    </div>
                  );
                }}
              />
            </FormField>
            <FormField label={t("Select Bank")} name="issuerName">
              <Dropdown
                data-testid="select-duitnow-pay-bank"
                options={getIssuers(duitnowPayMode)}
                keyExtractor={(option) => (option as IssuerOption).value}
                value={values.selectedBank}
                onChange={async (selectedOption: unknown) => {
                  setFieldValue("selectedBank", selectedOption);
                }}
                block
                placeholder={t("Select Bank")}
                renderOption={(option, helpers) => {
                  const issuer = option as IssuerOption;

                  return (
                    <div className="w-full">
                      <DropdownOption
                        {...helpers}
                        className="flex items-center space-x-2"
                        data-testid={kebabCase(issuer.label.toLowerCase())}
                      >
                        <span>{issuer.name}</span>
                      </DropdownOption>
                    </div>
                  );
                }}
                renderSelection={(selection) => {
                  const issuer = selection as IssuerOption;

                  return (
                    <div className="flex items-center space-x-2">
                      <span>{issuer.display_name}</span>
                    </div>
                  );
                }}
                optionDisabled={(option) => (option as IssuerOption).disabled}
              />
            </FormField>

            {values.selectedBank ? (
              <>
                <Button
                  block
                  type="submit"
                  variant="brand-secondary"
                  className="mt-4 flex-1 md:flex-initial"
                  data-testid="continue"
                >
                  {t("Continue")}
                </Button>
              </>
            ) : null}
          </form>
        </div>
      </div>
    </>
  );
};

export default DuitnowPayAccordionContent;
